import { type RefObject, useContext, useEffect, useRef, useState } from "react"
import { ChartContext } from "./ChartContext"
import { chartCSSVars } from "./chartSettingColors"
import { getColors } from "./colors"
import { findPointAtX } from "./findPointAtX"
import { formatTooltip, type TooltipOptions } from "./Tooltip"
import type { OnChartPointClick } from "./types"
import type { Highcharts } from "./useHighcharts"
import { useHighcharts } from "./useHighcharts"
import { attachCrosshairEndpoints } from "./utils/crosshairEndpoints"
import { extractTooltipData } from "./utils/extractTooltipData"
import { MARKER_GROUP_Z_INDEX } from "./utils/plotOverlays"

declare module "highcharts" {
  interface Series {
    /** The `<g>` holding this series' point markers. Present at runtime but
     * absent from the published types; the chart `render` handler below moves
     * it onto the chart root so it outranks the crosshairs. */
    markerGroup?: Highcharts.SVGElement
  }
}

/**
 * `parentGroup` is the handle Highcharts' own `zIndexSetter` reads to decide
 * which element to sort the node into. Clearing it makes the next `zIndex`
 * write re-sort the node against the chart root rather than the series group.
 * Runtime-only, like `markerGroup` above.
 */
type ReparentableGroup = Highcharts.SVGElement & {
  parentGroup?: unknown
  zIndex?: number
}

const NAVIGATOR_HANDLE = (
  _x: number,
  _y: number,
  w: number,
  h: number,
  options: { width?: number; height?: number; radius?: number }
) => {
  const halfWidth = options.width ? options.width / 2 : w
  const markerPosition = Math.round(halfWidth / 4) + 0.5
  const round = options.radius || 2
  const height = options.height || h

  /*
    How this was done:

    Quote from Sami:
    There really isn't a proper documentation for from highcharts,
    but we used the "symbols" option for handles (https://api.highcharts.com/highstock/navigator.handles.symbols)

    In the docs they give this demo:
    https://jsfiddle.net/gh/get/library/pure/highcharts/highcharts/tree/master/samples/stock/navigator/styled-handles/

    And with some tweaking and help from the Highcharts support team it was possible to build what is seen possible.
  */

  return [
    ["M", -halfWidth - 1 + round, 0.5],
    ["L", halfWidth - round, 0.5],
    ["C", halfWidth, 0.5, halfWidth, 0.5 + round, halfWidth, 0.5 + round],
    ["L", halfWidth, height + 0.5 - round],
    [
      "C",
      halfWidth,
      height + 0.5 - round,
      halfWidth,
      height + 0.5,
      halfWidth - round,
      height + 0.5,
    ],
    ["L", -halfWidth - 1 + round, height + 0.5],
    [
      "C",
      -halfWidth - 1,
      height + 0.5,
      -halfWidth - 1,
      height + 0.5 - round,
      -halfWidth - 1,
      height + 0.5 - round,
    ],
    ["L", -halfWidth - 1, 0.5 + round],
    [
      "C",
      -halfWidth - 1,
      0.5,
      -halfWidth - 1 + round,
      0.5,
      -halfWidth - 1 + round,
      0.5,
    ],
    ["M", -markerPosition, 4],
    ["L", -markerPosition, height - 3],
    ["M", markerPosition - 1, 4],
    ["L", markerPosition - 1, height - 3],
  ]
}

export const DEFAULT_OPTIONS: Partial<Highcharts.Options> = {
  chart: {
    animation: false,
    alignTicks: true,
    alignThresholds: true,
    style: {
      fontFamily: chartCSSVars.fontFamily,
      fontWeight: "400",
    },
    backgroundColor: "transparent",
    spacing: [0, 5, 0, 5] as Array<number>,
    reflow: false,
  },
  colors: getColors(10),
  credits: {
    enabled: false,
  },
  accessibility: {
    enabled: false,
  },
  legend: {
    align: "center",
    verticalAlign: "top",
    squareSymbol: false,
    symbolHeight: 12,
    symbolWidth: 12,
    symbolPadding: 10,
    symbolRadius: 1,
    itemStyle: {
      color: chartCSSVars.legend.color,
      fontWeight: chartCSSVars.legend.fontWeight,
    },
    itemHoverStyle: {
      color: chartCSSVars.legend.color,
    },
    itemHiddenStyle: {
      opacity: 0.666,
    },
  },
  navigator: {
    handles: {
      symbols: ["roundedBox", "roundedBox"],
      width: 9,
      height: 17,
      backgroundColor: chartCSSVars.navigator.backgroundColor,
      borderColor: chartCSSVars.navigator.borderColor,
    },
    outlineColor: chartCSSVars.navigator.outlineColor,
    outlineWidth: 1,
    xAxis: {
      gridLineColor: chartCSSVars.axis.gridColor,
    },
    maskFill: chartCSSVars.navigator.maskFill,
  },
  plotOptions: {
    area: {
      fillOpacity: 0.3,
      connectEnds: true,
      connectNulls: true,
      zIndex: 5,
      lineWidth: 1.75,
    },
    column: {
      crisp: false,
      dataLabels: {
        padding: 0,
      },
      borderColor: "unset",
      opacity: 1, // Ensures that eg. Bitcoin with 14y data at max zoom + day granularity doesnt become solid bar
    },
    line: {
      zIndex: 10,
    },
    series: {
      animation: false,
      showInNavigator: true,
      label: {
        style: {
          fontWeight: "500",
        },
      },
      states: {
        hover: {
          // Highcharts draws a soft translucent glow around the hovered point
          // by default. It reads as a smudge rather than a deliberate mark,
          // and on a stacked chart where every band shows a dot at once it
          // multiplies into a row of blobs. The crosshair already carries the
          // "you are here" signal, so the dot alone is enough.
          halo: null,
        },
        inactive: {
          // Highcharts marks a series inactive when the hovered date carries no
          // point for it, and fades it to 0.2 by default. On a stacked area
          // chart a ghost at 0.2 still occupies its slot in the stack, so the
          // bands that DO have data appear to float above a grey remainder
          // rather than reading as the whole of that date. Dropping it to 0
          // makes the hover answer exactly one question: what existed then.
          //
          // This is the built-in state only. Legend hover deliberately does not
          // use it — `applySeriesHover` sets its own 0.15 group opacity so the
          // dimmed bands stay legible while you read down the list — so that
          // behaviour is unaffected.
          opacity: 0,
        },
      },
    },
  },
  rangeSelector: {
    enabled: false,
  },
  scrollbar: {
    enabled: false,
  },
  title: {
    text: ``,
    margin: 0,
  },
  tooltip: {
    className: "highcharts-tooltip-container2",
    borderColor: "transparent",
    followPointer: true,
    borderRadius: 10, // Due to svg, border-radius via CSS doesn't seem to do exactly what we want. Even this feels slightly off, especially in top left corner
    shadow: {
      offsetX: 0,
      offsetY: 0,
      color: "black",
      opacity: 0.00666,
      width: 9,
    },
    formatter(
      tooltip: Parameters<Highcharts.TooltipFormatterCallbackFunction>[0]
    ): ReturnType<Highcharts.TooltipFormatterCallbackFunction> {
      return formatTooltip(this, tooltip)
    },
    headerFormat: `{point.key}`,
    outside: true,
    padding: 0,
    shared: true,
    split: false,
    useHTML: true,
    style: {
      zIndex: 100000, // Support tooltip on top of expanded chart
      color: "inherit",
    },
    animation: false,
    hideDelay: 0,
  },
  exporting: {
    enabled: false,
  },
}

/** @deprecated */
export function useInitializeHighcharts(
  el: RefObject<HTMLDivElement | null>,
  {
    chartType,
    showNavigator,
    tooltipOptions,
    legend,
    isBarChart,
    hasPercentageSeries,
    onChartPointClick,
  }: {
    chartType: "chart" | "stockChart"
    showNavigator: boolean
    tooltipOptions: Partial<TooltipOptions>
    legend: boolean
    isBarChart?: boolean
    hasPercentageSeries: boolean
    onChartPointClick?: OnChartPointClick
  }
) {
  const { _events } = useContext(ChartContext)
  const tooltipOptionsRef = useRef(tooltipOptions)
  const onChartPointClickRef = useRef(onChartPointClick)
  const highchartsInstance = useHighcharts()
  const chartRef = useRef<Highcharts.Chart | null>(null)
  const [, setIsMounted] = useState(false)

  tooltipOptionsRef.current = tooltipOptions
  onChartPointClickRef.current = onChartPointClick

  useEffect(() => {
    const container = el.current
    if (!highchartsInstance || !container) {
      return
    }

    const chartOptions: Highcharts.Options = {
      ...DEFAULT_OPTIONS,
      navigator: {
        ...DEFAULT_OPTIONS.navigator,
        enabled: showNavigator && chartType === "stockChart",
      },
      legend: {
        ...DEFAULT_OPTIONS.legend,
        enabled: legend,
      },
      tooltip: {
        ...DEFAULT_OPTIONS.tooltip,
        formatter(
          tooltip: Parameters<Highcharts.TooltipFormatterCallbackFunction>[0]
        ): ReturnType<Highcharts.TooltipFormatterCallbackFunction> {
          return formatTooltip(this, tooltip, {
            sort: false,
            showTotal: false,
            ...tooltipOptionsRef.current,
          })
        },
      },
      chart: {
        ...DEFAULT_OPTIONS.chart,
        spacingTop: legend ? undefined : 25,
        events: {
          redraw: function () {
            _events.current?.redraw?.forEach((cb) => cb.call(this))
          },
          load: function () {
            // Dots where each hover crosshair meets its axis. Attached on load
            // so they are created once per chart and destroyed with its SVG.
            attachCrosshairEndpoints(this)
            _events.current?.load?.forEach((cb) => cb.call(this))
          },
          addSeries: function (e) {
            _events.current?.addSeries?.forEach((cb) => cb.call(this, [e]))
          },
          render: function () {
            // Highcharts parents every series' markerGroup to the shared
            // series group, which sits at z-index 3 — below the hover
            // crosshairs. Nothing set on the marker group can change that: a
            // z-index only sorts a node against its own siblings, so the
            // crosshair was painted straight through each hover dot.
            //
            // Re-parenting the group onto the chart root is what makes the two
            // comparable at all. Safe because the group carries its own
            // `translate(plotLeft, plotTop)` and inherits neither a transform
            // nor a clip path from the series group, so it lands in the same
            // place; see `utils/plotOverlays.ts` for the resulting order.
            const root = this.renderer.box
            for (const serie of this.series) {
              const markerGroup = serie.markerGroup as
                | ReparentableGroup
                | undefined
              // Keyed on the actual parent, not on the z-index: Highcharts
              // re-creates and re-parents the group on some redraws without
              // touching the value, and a z-index check would then skip a
              // group that had fallen back into the series group. `render`
              // fires on every redraw, pan and resize, so this has to be a
              // no-op once the node is already in place.
              if (!markerGroup || markerGroup.element.parentNode === root) {
                continue
              }
              markerGroup.parentGroup = undefined
              markerGroup.attr({ zIndex: MARKER_GROUP_Z_INDEX })
            }
            _events.current?.render?.forEach((cb) => cb.call(this))
          },
        },
      },
    }

    if (isBarChart) {
      chartOptions.chart = {
        ...DEFAULT_OPTIONS.chart,
        type: "bar",
      }
    }

    if (hasPercentageSeries && chartOptions.chart) {
      chartOptions.chart.alignTicks = false
      chartOptions.chart.alignThresholds = false
    }

    // Add click handler for both points and chart area

    // Point click handler (when clicking directly on a bar/point)
    if (chartOptions.plotOptions?.series) {
      chartOptions.plotOptions.series.cursor = "pointer"
      chartOptions.plotOptions.series.point = {
        events: {
          click: (event: Highcharts.PointClickEventObject) => {
            event.stopPropagation()
            if (!onChartPointClickRef.current) return

            const tooltipData = extractTooltipData(
              event.point,
              // @ts-expect-error - tooltipOptionsRef.current is a Partial<TooltipOptions>
              tooltipOptionsRef.current
            )
            onChartPointClickRef.current(tooltipData)
          },
        },
      }
    }

    // Chart area click handler (when clicking in empty space)
    if (chartOptions.chart) {
      chartOptions.chart.events = {
        ...chartOptions.chart.events,
        click: function (event: Highcharts.PointerEventObject) {
          const chart = this as Highcharts.Chart
          if (!onChartPointClickRef.current) return

          // Get the X-axis position from the click
          const xAxis = chart.xAxis[0]
          if (!xAxis) {
            return
          }

          const clickedX = xAxis.toValue(event.chartX)
          const pointAtX = findPointAtX(chart, clickedX)
          if (!pointAtX) {
            return
          }

          // Extract complete tooltip data for this X position
          const tooltipData = extractTooltipData(
            pointAtX,
            // @ts-expect-error - tooltipOptionsRef.current is a Partial<TooltipOptions>
            tooltipOptionsRef.current
          )

          onChartPointClickRef.current?.(tooltipData)
        },
      }
    }

    // @ts-expect-error - SVGRendere is a special trick - https://jsfiddle.net/gh/get/library/pure/highcharts/highcharts/tree/master/samples/stock/navigator/styled-handles/
    highchartsInstance.SVGRenderer.prototype.symbols.roundedBox =
      NAVIGATOR_HANDLE
    chartRef.current = highchartsInstance[chartType](container, chartOptions)
    chartRef.current.redraw(false)
    setIsMounted(true)

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy()
      }

      setIsMounted(false)
      chartRef.current = null
    }
    // Explicitly exclude hasPercentageSeries for some reason
  }, [
    highchartsInstance,
    chartType,
    showNavigator,
    el,
    legend,
    _events,
    isBarChart,
    hasPercentageSeries,
  ])

  return chartRef
}
